import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';

import Modal from '../../containers/modal.jsx';
import Box from '../box/box.jsx';
import Button from '../button/button.jsx';

import styles from './feedback-modal.css';

class FeedbackModal extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            name: '',
            phone: '',
            email: '',
            comments: '',
            errors: {},
            isSubmitting: false,
            submitSuccess: false,
            submitError: null
        };
        this.handleNameChange = this.handleNameChange.bind(this);
        this.handlePhoneChange = this.handlePhoneChange.bind(this);
        this.handleEmailChange = this.handleEmailChange.bind(this);
        this.handleCommentsChange = this.handleCommentsChange.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.validateForm = this.validateForm.bind(this);
    }

    handleNameChange(e) {
        this.setState({ name: e.target.value, errors: { ...this.state.errors, name: null } });
    }

    handlePhoneChange(e) {
        this.setState({ phone: e.target.value, errors: { ...this.state.errors, phone: null } });
    }

    handleEmailChange(e) {
        this.setState({ email: e.target.value, errors: { ...this.state.errors, email: null } });
    }

    handleCommentsChange(e) {
        this.setState({ comments: e.target.value, errors: { ...this.state.errors, comments: null } });
    }

    validateForm() {
        const errors = {};
        const { name, phone, email, comments } = this.state;

        if (!name.trim()) {
            errors.name = 'Name is required';
        }

        if (!phone.trim()) {
            errors.phone = 'Phone number is required';
        } else if (!/^\d{10}$/.test(phone.replace(/[\s-]/g, ''))) {
            errors.phone = 'Please enter a valid 10-digit phone number';
        }

        if (!email.trim()) {
            errors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            errors.email = 'Please enter a valid email address';
        }

        if (!comments.trim()) {
            errors.comments = 'Comments are required';
        }

        return errors;
    }

    async handleSubmit(e) {
        e.preventDefault();
        
        const errors = this.validateForm();
        if (Object.keys(errors).length > 0) {
            this.setState({ errors });
            return;
        }

        this.setState({ isSubmitting: true, submitError: null });

        try {
            const feedbackData = {
                name: this.state.name,
                phone: this.state.phone,
                email: this.state.email,
                comments: this.state.comments
            };

            // API endpoint - Change to production URL when deploying
            const apiEndpoint = 'https://app.tifstudio.in/api/feedback';
            
            const response = await fetch(apiEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(feedbackData)
            });

            if (!response.ok) {
                throw new Error('Failed to submit feedback');
            }

            this.setState({
                isSubmitting: false,
                submitSuccess: true,
                name: '',
                phone: '',
                email: '',
                comments: ''
            });

            // Close modal after 2 seconds
            setTimeout(() => {
                this.props.onRequestClose();
                this.setState({ submitSuccess: false });
            }, 2000);

        } catch (error) {
            console.error('Error submitting feedback:', error);
            this.setState({
                isSubmitting: false,
                submitError: 'Failed to submit feedback. Please try again.'
            });
        }
    }

    render() {
        const { isSubmitting, submitSuccess, submitError, errors } = this.state;

        return (
            <Modal
                className={styles.modalContent}
                contentLabel="Feedback"
                overlayClassName={styles.whiteOverlay}
                onRequestClose={this.props.onRequestClose}
            >
                <Box className={styles.body}>
                    <div className={styles.feedbackForm}>
                        {submitSuccess ? (
                            <div className={styles.successMessage}>
                                <FormattedMessage
                                    defaultMessage="Thank you for your feedback!"
                                    description="Success message after feedback submission"
                                    id="gui.feedbackModal.success"
                                />
                            </div>
                        ) : (
                            <form onSubmit={this.handleSubmit}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="feedback-name" className={styles.label}>
                                        <FormattedMessage
                                            defaultMessage="Name"
                                            description="Label for name field"
                                            id="gui.feedbackModal.name"
                                        />
                                        <span className={styles.required}>*</span>
                                    </label>
                                    <input
                                        className={classNames(styles.input, {
                                            [styles.inputError]: errors.name
                                        })}
                                        id="feedback-name"
                                        type="text"
                                        value={this.state.name}
                                        onChange={this.handleNameChange}
                                        placeholder="Enter your name"
                                    />
                                    {errors.name && <span className={styles.errorText}>{errors.name}</span>}
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="feedback-phone" className={styles.label}>
                                        <FormattedMessage
                                            defaultMessage="Phone Number"
                                            description="Label for phone field"
                                            id="gui.feedbackModal.phone"
                                        />
                                        <span className={styles.required}>*</span>
                                    </label>
                                    <input
                                        className={classNames(styles.input, {
                                            [styles.inputError]: errors.phone
                                        })}
                                        id="feedback-phone"
                                        type="tel"
                                        value={this.state.phone}
                                        onChange={this.handlePhoneChange}
                                        placeholder="Enter your phone number"
                                    />
                                    {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="feedback-email" className={styles.label}>
                                        <FormattedMessage
                                            defaultMessage="Email"
                                            description="Label for email field"
                                            id="gui.feedbackModal.email"
                                        />
                                        <span className={styles.required}>*</span>
                                    </label>
                                    <input
                                        className={classNames(styles.input, {
                                            [styles.inputError]: errors.email
                                        })}
                                        id="feedback-email"
                                        type="email"
                                        value={this.state.email}
                                        onChange={this.handleEmailChange}
                                        placeholder="Enter your email"
                                    />
                                    {errors.email && <span className={styles.errorText}>{errors.email}</span>}
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="feedback-comments" className={styles.label}>
                                        <FormattedMessage
                                            defaultMessage="Comments"
                                            description="Label for comments field"
                                            id="gui.feedbackModal.comments"
                                        />
                                        <span className={styles.required}>*</span>
                                    </label>
                                    <textarea
                                        className={classNames(styles.textarea, {
                                            [styles.inputError]: errors.comments
                                        })}
                                        id="feedback-comments"
                                        value={this.state.comments}
                                        onChange={this.handleCommentsChange}
                                        placeholder="Enter your feedback or comments"
                                        rows="5"
                                    />
                                    {errors.comments && <span className={styles.errorText}>{errors.comments}</span>}
                                </div>

                                {submitError && (
                                    <div className={styles.errorMessage}>
                                        {submitError}
                                    </div>
                                )}

                                <div className={styles.buttonGroup}>
                                    <Button
                                        className={styles.cancelButton}
                                        onClick={this.props.onRequestClose}
                                    >
                                        <FormattedMessage
                                            defaultMessage="Cancel"
                                            description="Cancel button"
                                            id="gui.feedbackModal.cancel"
                                        />
                                    </Button>
                                    <Button
                                        className={styles.submitButton}
                                        onClick={this.handleSubmit}
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <FormattedMessage
                                                defaultMessage="Submitting..."
                                                description="Submitting button text"
                                                id="gui.feedbackModal.submitting"
                                            />
                                        ) : (
                                            <FormattedMessage
                                                defaultMessage="Submit Feedback"
                                                description="Submit button"
                                                id="gui.feedbackModal.submit"
                                            />
                                        )}
                                    </Button>
                                </div>
                            </form>
                        )}
                    </div>
                </Box>
            </Modal>
        );
    }
}

FeedbackModal.propTypes = {
    intl: intlShape.isRequired,
    onRequestClose: PropTypes.func.isRequired
};

export default injectIntl(FeedbackModal);
